import BaseService from './BaseService';
import * as apiroutes from '../apiRoutes';

class S3FileRetrievalService extends BaseService {
    constructor() {
        super(apiroutes.EVIDANCE_ITEMS_DOWNLOAD)
    }

    /**
     * Get download URL for a file using its S3 key
     * @param {string} s3Key - The S3 key of the file to retrieve
     * @returns {Promise<Object>} Response with download URL
     */
    async getDownloadUrl(s3Key) {
        try {
            if (!s3Key) {
                throw new Error('S3 key is required');
            }

            console.log('Making API call for s3Key:', s3Key);
            console.log('API endpoint:', `${this.baseURL}?key=${encodeURIComponent(s3Key)}`);

            // Make API call to get download URL - no need for /download-url path since it's in the base URL
            const response = await super.customCall('GET', `?key=${encodeURIComponent(s3Key)}`, null, null, 'json');

            console.log('API response:', response);

            if (response.success && response.data?.url) {
                console.log('Successfully got download URL:', response.data.url);
                return {
                    success: true,
                    data: {
                        url: response.data.url,
                        key: s3Key
                    },
                    message: 'Download URL retrieved successfully'
                };
            } else {
                console.error('API call failed - response:', response);
                throw new Error(response.message || response.error || 'Failed to get download URL from API');
            }
        } catch (error) {
            console.error('Error getting download URL:', error);
            console.error('Error details:', {
                message: error.message,
                stack: error.stack,
                response: error.response
            });
            return {
                success: false,
                error: error.message || 'Failed to get download URL',
                message: 'Failed to retrieve download URL from server'
            };
        }
    }

    /**
     * Get download URLs for multiple files
     * @param {Array<string>} s3Keys - Array of S3 keys
     * @returns {Promise<Object>} Response with multiple download URLs
     */
    async getMultipleDownloadUrls(s3Keys) {
        try {
            if (!Array.isArray(s3Keys) || s3Keys.length === 0) {
                throw new Error('Array of S3 keys is required');
            }

            const results = await Promise.allSettled(
                s3Keys.map(key => this.getDownloadUrl(key))
            );

            const successful = [];
            const failed = [];

            results.forEach((result, index) => {
                if (result.status === 'fulfilled' && result.value.success) {
                    successful.push({
                        key: s3Keys[index],
                        url: result.value.data.url,
                        index
                    });
                } else {
                    failed.push({
                        key: s3Keys[index],
                        error: result.status === 'fulfilled' ? result.value.error : result.reason?.message,
                        index
                    });
                }
            });

            return {
                success: failed.length === 0,
                data: {
                    successful,
                    failed,
                    summary: {
                        total: s3Keys.length,
                        successCount: successful.length,
                        failCount: failed.length
                    }
                },
                message: failed.length === 0
                    ? 'All download URLs retrieved successfully'
                    : `${successful.length} of ${s3Keys.length} URLs retrieved successfully`
            };
        } catch (error) {
            console.error('Error getting multiple download URLs:', error);
            return {
                success: false,
                error: error.message || 'Failed to get download URLs',
                message: 'Failed to retrieve multiple download URLs'
            };
        }
    }

    /**
     * Download a file directly to the browser
     * @param {string} s3Key - The S3 key of the file
     * @param {string} filename - Optional custom filename for download
     * @returns {Promise<Object>} Download response
     */
    async downloadFile(s3Key, filename = null) {
        try {
            // Get download URL first
            const urlResponse = await this.getDownloadUrl(s3Key);

            if (!urlResponse.success) {
                throw new Error(urlResponse.error || 'Failed to get download URL');
            }

            const downloadUrl = urlResponse.data.url;

            // Create a temporary anchor element to trigger download
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.target = '_blank';

            // Set custom filename if provided
            if (filename) {
                link.download = filename;
            }

            // Append to body, click, and remove
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            return {
                success: true,
                message: 'File download initiated successfully'
            };
        } catch (error) {
            console.error('Error downloading file:', error);
            return {
                success: false,
                error: error.message || 'Failed to download file',
                message: 'Failed to initiate file download'
            };
        }
    }

    /**
     * Download a file directly from a URL
     * @param {string} url - The download URL
     * @param {string} filename - Optional custom filename for download
     * @returns {Promise<Object>} Download response
     */
    async downloadFromUrl(url, filename = null) {
        try {
            if (!url) {
                throw new Error('Download URL is required');
            }

            console.log('Downloading file from URL:', url);
            console.log('Filename:', filename);

            // Create a temporary anchor element to trigger download
            const link = document.createElement('a');
            link.href = url;
            link.target = '_blank';

            // Set custom filename if provided
            if (filename) {
                link.download = filename;
            }

            // Append to body, click, and remove
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            console.log('Download initiated successfully');

            return {
                success: true,
                message: 'File download initiated successfully'
            };
        } catch (error) {
            console.error('Error downloading file from URL:', error);
            return {
                success: false,
                error: error.message || 'Failed to download file from URL',
                message: 'Failed to initiate file download from URL'
            };
        }
    }

    /**
     * Get file metadata from S3 key
     * @param {string} s3Key - The S3 key
     * @returns {Object} File metadata
     */
    getFileMetadata(s3Key) {
        try {
            // Extract information from S3 key
            // Expected format: evidence/temp-uuid/evidence-id/timestamp_filename.ext
            const parts = s3Key.split('/');
            const filename = parts[parts.length - 1];
            const [timestamp, ...nameParts] = filename.split('_');
            const originalName = nameParts.join('_');

            // Get file extension
            const extension = filename.split('.').pop()?.toLowerCase();

            // Determine file type
            let fileType = 'document';
            if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) {
                fileType = 'image';
            } else if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(extension)) {
                fileType = 'video';
            } else if (['mp3', 'wav', 'ogg', 'aac', 'm4a'].includes(extension)) {
                fileType = 'audio';
            } else if (['pdf'].includes(extension)) {
                fileType = 'pdf';
            }

            return {
                key: s3Key,
                filename: originalName || filename,
                originalFilename: filename,
                extension,
                fileType,
                timestamp,
                path: parts.slice(0, -1).join('/'),
                isValid: Boolean(filename && extension)
            };
        } catch (error) {
            console.error('Error parsing file metadata:', error);
            return {
                key: s3Key,
                filename: s3Key,
                originalFilename: s3Key,
                extension: null,
                fileType: 'unknown',
                timestamp: null,
                path: null,
                isValid: false,
                error: error.message
            };
        }
    }

    /**
     * Check if file URL is still valid (not expired)
     * @param {string} url - The S3 pre-signed URL
     * @returns {boolean} True if URL appears to be valid
     */
    isUrlValid(url) {
        try {
            const urlObj = new URL(url);
            const params = new URLSearchParams(urlObj.search);
            const expires = params.get('X-Amz-Expires');
            const date = params.get('X-Amz-Date');

            if (expires && date) {
                const expirationTime = new Date(
                    date.replace(/(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z/,
                        '$1-$2-$3T$4:$5:$6Z')
                ).getTime() + (parseInt(expires) * 1000);

                return Date.now() < expirationTime;
            }

            return true; // If we can't determine expiration, assume it's valid
        } catch (error) {
            console.error('Error checking URL validity:', error);
            return false;
        }
    }

    /**
     * Get file size from URL by making a HEAD request
     * @param {string} url - The file URL
     * @returns {Promise<number|null>} File size in bytes or null if failed
     */
    async getFileSize(url) {
        try {
            const response = await fetch(url, { method: 'HEAD' });
            if (response.ok) {
                const contentLength = response.headers.get('content-length');
                return contentLength ? parseInt(contentLength) : null;
            }
            return null;
        } catch (error) {
            console.error('Error getting file size:', error);
            return null;
        }
    }

    /**
     * Format file size in human readable format
     * @param {number} bytes - File size in bytes
     * @returns {string} Formatted file size
     */
    formatFileSize(bytes) {
        if (bytes === 0) return '0 B';
        if (!bytes) return 'Unknown size';

        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    }

    /**
     * Extract filename from S3 key
     * @param {string} s3Key - The S3 key
     * @returns {string} Extracted filename
     */
    extractFilename(s3Key) {
        if (!s3Key) return 'Unknown file';
        const parts = s3Key.split('/');
        return parts[parts.length - 1] || 'Unknown file';
    }

    /**
     * Get file type from S3 key/filename
     * @param {string} s3Key - The S3 key
     * @returns {string} File type category
     */
    getFileType(s3Key) {
        if (!s3Key) return 'unknown';

        const extension = this.getFileExtension(s3Key);

        // Image types
        if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) {
            return 'image';
        }

        // Video types
        if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(extension)) {
            return 'video';
        }

        // Audio types
        if (['mp3', 'wav', 'flac', 'aac', 'ogg', 'm4a'].includes(extension)) {
            return 'audio';
        }

        // PDF
        if (extension === 'pdf') {
            return 'pdf';
        }

        // Archive types
        if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) {
            return 'archive';
        }

        // Document types
        if (['doc', 'docx', 'txt', 'rtf'].includes(extension)) {
            return 'document';
        }

        return 'file';
    }

    /**
     * Get file extension from S3 key/filename
     * @param {string} s3Key - The S3 key
     * @returns {string} File extension
     */
    getFileExtension(s3Key) {
        if (!s3Key) return '';

        const filename = this.extractFilename(s3Key);
        const lastDotIndex = filename.lastIndexOf('.');

        if (lastDotIndex === -1 || lastDotIndex === filename.length - 1) {
            return '';
        }

        return filename.substring(lastDotIndex + 1).toLowerCase();
    }

}

// Create instance and export it
const S3FileRetrievalServiceInstance = new S3FileRetrievalService();
export default S3FileRetrievalServiceInstance;